/**
 * AppV2 — minimal React shell.
 *
 * All business logic lives in app-runtime.ts (pure TS, zero React imports).
 * This component only: creates the runtime, subscribes to state changes,
 * and renders the element tree via RenderContext.
 *
 * Block-Centric Lifecycle:
 * - Runtime is created ONCE via useRef (not useMemo) — survives blocks changes.
 * - blocks prop changes → addBlocks() — only new blocks are processed, existing state preserved.
 * - Generation counter protects against StrictMode double-mount race conditions.
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { RenderContext, BreakpointProvider, getIsolatedAppStyle, renderUnmappedType } from "@cogo/react-renderer"
import type { RenderContextValue, CogoBlockDef, IslandRegistry, ElementRegistry } from "@cogo/react-renderer"
import type { App, BlockDef } from "@cogo/types-v2"
import type { PlatformAdapter, ToastHandler } from "@cogo/runtime-v2"
import { StateManager, ActionFlowEngine } from "@cogo/runtime-v2"
import {
  resolveThemeCssVarsForScheme,
  type ResolvedTheme,
} from "@cogo/design-presets"
import { createAppRuntime, getSharedRegistry } from "../runtime/app-runtime"
import type { AppRuntime, ThemeTokens } from "../runtime/app-runtime"
import { createHostServices, type HostServices } from "../host/host-services"
import { resolveMotionBlockId } from "../host/motion-registry"
import { ModalHost, type ModalComponentRegistry } from "../host/modal-host"
import type { AppSandbox } from "../sandbox/app-sandbox.types"
import { AtomDefaultsProvider } from "../context/atom-defaults-context"
import { LayoutWrapper, type Layout } from "./layouts"
import {
  materializeBlocksFromRenderPackBytes,
  type AppRenderPackBytes,
} from "../runtime/render-pack-transport"

export interface AppV2Props {
  manifest: App
  blocks: Record<string, BlockDef>
  platform?: PlatformAdapter
  /** AppSandbox instance — attaches sandbox modules to PlatformAdapter for runtime isolation */
  sandbox?: AppSandbox | null
  onToast?: ToastHandler
  initialRoute?: string
  onNavigate?: (route: string) => void | Promise<void>
  onReady?: (ctx: { stateManager: StateManager; actionEngine: ActionFlowEngine; hostServices?: HostServices }) => void
  /**
   * Host services backing host-effect actions (emitEvent / openModal /
   * invokeCallback / mediaControl / runMotion). Provide your own to subscribe to the event
   * bus / register callbacks / register media elements. Browser rendering
   * auto-creates a bundle when omitted so motion remains available.
   */
  hostServices?: HostServices
  /** modalId → component map rendered by the built-in <ModalHost> for `openModal`. */
  modalRegistry?: ModalComponentRegistry
  designSystem?: Record<string, string>
  /** Theme tokens (colors + radius) — scoped to the app wrapper/portal, not host :root */
  themeTokens?: ThemeTokens
  /** Resolved design-system theme — cssVars merged on top of themeTokens; atomProps feed AtomDefaultsProvider */
  theme?: ResolvedTheme
  /** Optional agentic page spec — wraps rendered blocks in a layout (stack/2col/grid) */
  pageSpec?: { blocks: string[]; layout: Layout; context: Record<string, unknown> }
  /** Studio-only Agent shell supplied by the default renderer export. */
  agentSlot?: React.ReactNode
  /** Optional block-name keyed RPK1 bytes. When present, AppV2 materializes UI roots from bytes before runtime creation. */
  renderPackBytes?: AppRenderPackBytes
  /** Color scheme — light or dark mode */
  colorScheme?: 'light' | 'dark'
  /**
   * Embedded hosts such as Cogo Studio must provide an AppSandbox so app
   * tokens/classes are never written onto the shared document root/body.
   * Standalone app runtimes may omit this and use the document as their app
   * document.
   */
  requireDesignSandbox?: boolean
  /**
   * Optional host-declared island registry (`type: "island"` name → React
   * component). Threaded into `RenderContext.islands` so a generated app's
   * island nodes mount (e.g. game / interactive-render islands). Back-compatible:
   * absent → the IslandRenderer warns + renders null, exactly as today.
   */
  islands?: IslandRegistry
  /**
   * Optional host override for an island's flow request. When omitted, AppV2
   * delivers a default `onFlow` that resolves the named ActionFlow from its own
   * runtime (ActionFlowEngine) and executes it. When provided, the host decides
   * what an island flow invocation does (e.g. log / route / custom dispatch).
   */
  onIslandFlow?: (flowName: string, eventType: string, payload?: unknown) => void
  /**
   * Optional element-renderer registry (workstream D). Omit → the shared
   * all-renderer registry (Studio/dev default, unchanged). Pass a lane-composed
   * registry (`composeElementRegistry(manifest.meta.renderProfile.lanes)`) for
   * deploy-selective rendering.
   */
  registry?: ElementRegistry
}

export const COGO_RUNTIME_EVENT_EXECUTION_EVENT = "cogo:runtime-event-execution:v1"

interface RuntimeReceiptVerifierChallenge {
  acceptedChallenge: string
  applicationHash: string
  bindingKey: string
  bindingKind: "action" | "flow"
  bindingRef: string
  completedChallenge: string
  loweringMapArtifactHash: string
  outputManifestHash: string
}

const runtimeReceiptVerifierChallenges = new Map<string, RuntimeReceiptVerifierChallenge>()
const activeRuntimeReceiptVerifierChallenges = new Map<number, RuntimeReceiptVerifierChallenge>()

declare global {
  interface Window {
    __cogoInstallRuntimeReceiptVerifierChallengeV1?: (
      input: RuntimeReceiptVerifierChallenge,
    ) => boolean
  }
}

function runtimeReceiptContentHash(value: unknown): value is string {
  return typeof value === "string" && /^sha256:[0-9a-f]{64}$/.test(value)
}

function runtimeReceiptChallenge(value: unknown): value is string {
  return typeof value === "string" && /^[0-9a-f]{64}$/.test(value)
}

if (typeof window !== "undefined") {
  window.__cogoInstallRuntimeReceiptVerifierChallengeV1 = (input) => {
    if (
      !input ||
      Object.keys(input).sort().join("\n") !==
        [
          "acceptedChallenge",
          "applicationHash",
          "bindingKey",
          "bindingKind",
          "bindingRef",
          "completedChallenge",
          "loweringMapArtifactHash",
          "outputManifestHash",
        ].join("\n") ||
      typeof input.bindingKey !== "string" ||
      (input.bindingKind !== "action" && input.bindingKind !== "flow") ||
      typeof input.bindingRef !== "string" ||
      !runtimeReceiptContentHash(input.applicationHash) ||
      !runtimeReceiptContentHash(input.outputManifestHash) ||
      !runtimeReceiptContentHash(input.loweringMapArtifactHash) ||
      !runtimeReceiptChallenge(input.acceptedChallenge) ||
      !runtimeReceiptChallenge(input.completedChallenge) ||
      input.acceptedChallenge === input.completedChallenge ||
      runtimeReceiptVerifierChallenges.has(input.bindingKey)
    ) {
      return false
    }
    runtimeReceiptVerifierChallenges.set(input.bindingKey, { ...input })
    return true
  }
}

export { getSharedRegistry }

export function AppV2({ manifest, blocks, platform, sandbox, onToast, initialRoute, onNavigate, onReady, designSystem, themeTokens, theme, pageSpec, agentSlot, renderPackBytes, colorScheme, hostServices, modalRegistry, requireDesignSandbox, islands, onIslandFlow, registry }: AppV2Props) {
  const [tick, forceUpdate] = useState(0)
  const runtimeRef = useRef<AppRuntime | null>(null)
  const designRoleKeysRef = useRef<Set<string>>(new Set())
  const initFlowsRanRef = useRef(false)
  const appRootRef = useRef<HTMLDivElement>(null)
  const renderPackBlockCount = renderPackBytes ? Object.keys(renderPackBytes).length : 0
  const runtimeBlocks = useMemo(
    () => materializeBlocksFromRenderPackBytes(blocks, renderPackBytes),
    [blocks, renderPackBytes],
  )
  const portalContainer = sandbox?.modules.dom.portalContainer ?? null

  // Resolve host services ONCE (stable across renders, like the runtime). Use the
  // caller's bundle if given; else auto-create one for the browser renderer so
  // runMotion works without bespoke host wiring. Headless runtimes still no-op.
  const hostServicesRef = useRef<HostServices | undefined>(undefined)
  const hostInitRef = useRef(false)
  if (!hostInitRef.current) {
    hostInitRef.current = true
    hostServicesRef.current = hostServices ?? createHostServices()
  }
  const effectiveHostServices = hostServicesRef.current

  // Compose platform adapter with sandbox modules (if provided)
  const effectivePlatform = useMemo(() => {
    if (!sandbox) return platform
    return { ...platform, sandbox: sandbox.modules } as PlatformAdapter
  }, [platform, sandbox])

  // Create runtime ONCE — never recreated on blocks changes.
  // If manifest changes (different app), we'd need a full remount via key prop.
  if (!runtimeRef.current) {
    runtimeRef.current = createAppRuntime(manifest, runtimeBlocks, {
      platform: effectivePlatform,
      onToast,
      initialRoute,
      onNavigate,
      onEventExecution: (receipt) => {
        const bindingKey = `${receipt.elementId}:${receipt.eventType}`
        let verifierChallenge: RuntimeReceiptVerifierChallenge | undefined
        if (receipt.status === "accepted") {
          verifierChallenge = runtimeReceiptVerifierChallenges.get(bindingKey)
          if (
            verifierChallenge &&
            (verifierChallenge.bindingKind !== receipt.bindingKind ||
              verifierChallenge.bindingRef !== receipt.bindingRef)
          ) {
            verifierChallenge = undefined
          }
          if (verifierChallenge) {
            runtimeReceiptVerifierChallenges.delete(bindingKey)
            activeRuntimeReceiptVerifierChallenges.set(receipt.executionId, verifierChallenge)
          }
        } else if (receipt.status === "completed") {
          verifierChallenge = activeRuntimeReceiptVerifierChallenges.get(receipt.executionId)
          if (verifierChallenge) {
            activeRuntimeReceiptVerifierChallenges.delete(receipt.executionId)
          }
        }
        appRootRef.current?.dispatchEvent(
          new CustomEvent(COGO_RUNTIME_EVENT_EXECUTION_EVENT, {
            detail: verifierChallenge
              ? {
                  ...receipt,
                  verificationApplicationHash: verifierChallenge.applicationHash,
                  verificationChallengeResponse:
                    receipt.status === "accepted"
                      ? verifierChallenge.acceptedChallenge
                      : verifierChallenge.completedChallenge,
                  verificationLoweringMapArtifactHash:
                    verifierChallenge.loweringMapArtifactHash,
                  verificationOutputManifestHash: verifierChallenge.outputManifestHash,
                }
              : receipt,
          }),
        )
      },
      designSystem,
      themeTokens,
      hostServices: effectiveHostServices,
      getMotionRoot: () => appRootRef.current,
      registry,
    })
  }
  const runtime = runtimeRef.current
  const currentPage = runtime.getCurrentPage()
  const motionBoundaryPageId = currentPage.id
  const motionBoundaryPageBlockKeys = JSON.stringify([...(currentPage.blocks ?? [])].sort())
  const blockRenderingExpectation = runtime.getBlockRenderingExpectation()
  const motionBoundaryRenderableBlockKeys = JSON.stringify(blockRenderingExpectation.renderableBlockKeys)
  const motionBoundaryBlockExclusions = JSON.stringify(blockRenderingExpectation.exclusions)
  const loadedBlockKeys = JSON.stringify(Object.keys(runtimeBlocks).sort())

  // `initialRoute` is also the controlled host-route input after mount. This
  // keeps a stable runtime/state graph while browser popstate/back/forward
  // changes the page rendered by the standalone publisher shell.
  useEffect(() => {
    if (initialRoute && runtime.currentRoute !== initialRoute) {
      void runtime.syncExternalRoute(initialRoute)
    }
  }, [initialRoute, runtime])

  // Design Ontology is live presentation authority. Updating a release writes
  // only the ds.* namespace and re-renders the same runtime/tree, so ActionFlow,
  // block state, input values, focus and scroll are not recreated.
  useEffect(() => {
    if (!designSystem) return
    const nextKeys = new Set(Object.keys(designSystem))
    for (const role of designRoleKeysRef.current) {
      if (!nextKeys.has(role)) runtime.stateManager.setStateTrusted(`ds.${role}`, "")
    }
    for (const [role, className] of Object.entries(designSystem)) {
      runtime.stateManager.setStateTrusted(`ds.${role}`, className)
    }
    designRoleKeysRef.current = nextKeys
    forceUpdate((n) => n + 1)
  }, [designSystem, runtime])

  // buildPageTree may place several block sections under one page-* root. Mark
  // each section (or an unwrapped block root such as a header) so block-scoped
  // host effects resolve beneath the correct manifest key and namespace.
  useLayoutEffect(() => {
    const root = appRootRef.current
    if (!root) return
    const boundaries = Array.from(root.querySelectorAll<HTMLElement>("[data-cogo-block-boundary]"))
    for (const boundary of boundaries) {
      const blockKey = boundary.getAttribute("data-cogo-block-boundary")
      if (!blockKey) continue
      const block = runtimeBlocks[blockKey]
      if (!block) continue
      const namespace = resolveMotionBlockId(blockKey, block)
      boundary.setAttribute("data-cogo-block-id", blockKey)
      boundary.setAttribute("data-cogo-block-namespace", namespace)
    }
  }, [runtimeBlocks, motionBoundaryPageId])

  // When blocks change (background loading), add only new blocks — runtime is NOT recreated.
  // Existing state (including async fetch results) is preserved.
  useEffect(() => {
    const added = runtime.addBlocks(runtimeBlocks)
    if (added.length > 0) {
      forceUpdate((n) => n + 1)
      // First block batch arrived after mount with empty blocks (full page reload):
      // run init flows with isRouteChange=true to apply state defaults.
      if (!initFlowsRanRef.current) {
        initFlowsRanRef.current = true
        runtime.runInitFlows()
      }
    }
  }, [runtimeBlocks, runtime])

  // Subscribe to state changes → re-render
  useEffect(() => runtime.subscribe(() => forceUpdate((n) => n + 1)), [runtime])

  // Register external actions (onReady) THEN run init flows.
  // Generation counter protects against StrictMode double-mount:
  //   Mount1: nextGeneration() → onReady → runInitFlows → unmount → nextGeneration() (invalidates)
  //   Mount2: nextGeneration() → onReady → runInitFlows (this one sticks)
  // Skip runInitFlows if blocks are empty — wait for addBlocks to bring the first batch.
  useEffect(() => {
    runtime.nextGeneration()
    onReady?.({ stateManager: runtime.stateManager, actionEngine: runtime.actionEngine, hostServices: effectiveHostServices })
    if (Object.keys(runtimeBlocks).length > 0) {
      initFlowsRanRef.current = true
      runtime.runInitFlows()
    }

    return () => {
      // StrictMode unmount: increment generation to invalidate stale async results
      runtime.nextGeneration()
    }
  }, [runtime])

  // Tick clock — the runtime half of the `tick` trigger. A requestAnimationFrame
  // loop fires all active trigger:"tick" flows each frame with event.dt (ms since
  // last frame), enabling real-time/tick-based logic (gravity, timers, simulations)
  // as pure ActionFlow. rAF auto-throttles to ~0 when the tab is hidden; an explicit
  // pause is honored via the symbol #{appData._system.tickPaused} (a flow can setState
  // it). No-op cost when an app has zero tick flows (runTickFlows returns immediately).
  useEffect(() => {
    let rafId = 0
    let last = performance.now()
    const loop = (now: number) => {
      const dt = now - last
      last = now
      if (!runtime.resolveSymbol("#{appData._system.tickPaused}")) {
        runtime.runTickFlows(dt)
      }
      rafId = requestAnimationFrame(loop)
    }
    rafId = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(rafId)
  }, [runtime])

  // True unmount — destroy runtime
  useEffect(() => () => {
    if (runtimeRef.current) {
      runtimeRef.current.destroy()
      runtimeRef.current = null
    }
  }, [])

  // Host media channel: lets the `type: "video"` renderer auto-register its live
  // <video> with the MediaRegistry so the `mediaControl` host effect drives it.
  // Absent when no host services (headless) → element still renders, control is a
  // no-op. Memoized on hostServices ALONE (not `tick`) so its identity is stable
  // across reactive state ticks — otherwise the video renderer's register effect
  // would churn (unregister/re-register) on every state change.
  const registerMedia = useMemo<RenderContextValue["registerMedia"]>(
    () =>
      effectiveHostServices
        ? (blockId: string, el: HTMLVideoElement) => effectiveHostServices.mediaRegistry.register(blockId, el)
        : undefined,
    [effectiveHostServices],
  )

  // Host flow channel: an island's `onFlow(flowBinding, eventType)` request
  // resolves the named ActionFlow from THIS app's runtime and executes it. A
  // host override (`onIslandFlow`) takes precedence. Memoized on
  // [runtime, onIslandFlow] (not `tick`) so injecting it into island props
  // doesn't churn on reactive state ticks. The flowBinding may be a bare flow
  // name or a `scheme://path/name` URI — we try the raw value then its last
  // path segment before giving up (best-effort, honest no-op + warn on miss).
  const islandOnFlow = useMemo<RenderContextValue["onFlow"]>(
    () =>
      onIslandFlow ??
      ((flowName: string, eventType: string, payload?: unknown) => {
        const engine = runtime.actionEngine
        const flow =
          engine.findFlow(flowName) ?? engine.findFlow(flowName.split("/").pop() ?? flowName)
        if (!flow) {
          console.warn(`[AppV2] island onFlow: no ActionFlow named "${flowName}"`)
          return
        }
        // Canonical flow event context is an OBJECT (app-runtime-core flowContext
        // .event), so the engine's `#{event.X}` resolver works (resolveEventRefs
        // bails on a non-object event). The island's eventType is exposed as
        // event.type; any payload is nested under event (it cannot clobber the
        // synthetic marker, and its keys are reachable as #{event.<key>}).
        const event: Record<string, unknown> = { type: eventType }
        if (payload && typeof payload === "object") {
          Object.assign(event, payload as Record<string, unknown>)
        } else if (payload !== undefined) {
          event.value = payload
        }
        // Mirror the engine's established contract everywhere else in the runtime:
        // a rejecting flow step (e.g. a restApi fetch failure) must NOT surface as
        // an unhandled rejection — catch + log instead of floating the promise.
        engine
          .executeFlow(flow, { event })
          .catch((err: unknown) =>
            console.error(`[AppV2] island onFlow: flow "${flowName}" failed`, err),
          )
      }),
    [runtime, onIslandFlow],
  )

  // RenderContext for child renderers
  const ctx = useMemo<RenderContextValue>(
    () => ({
      registry: runtime.registry,
      resolveSymbol: runtime.resolveSymbol.bind(runtime),
      dispatchEvent: runtime.dispatchEvent.bind(runtime),
      hasEventBinding: (elementId, eventType) =>
        Boolean(runtime.eventDispatcher.getBinding(elementId, eventType, runtime.activeBlocks)),
      navigate: runtime.navigate.bind(runtime),
      mode: "preview",
      blocks: runtimeBlocks as unknown as Record<string, CogoBlockDef>,
      designTokens: themeTokens as RenderContextValue["designTokens"],
      registerMedia,
      portalContainer,
      // DR-3: thread the host island registry + flow callback so a generated
      // app's `type:"island"` nodes mount and can delegate flows to the host.
      islands,
      onFlow: islandOnFlow,
    }),
    [runtime, runtimeBlocks, tick, themeTokens, registerMedia, portalContainer, islands, islandOnFlow],
  )

  // Build the page element tree (content-body injection done in runtime)
  // Placeholder blocks (type:'placeholder') are rendered through the same pipeline.
  const elements = useMemo(() => runtime.buildPageTree(), [runtime, tick])

  // Diagnostic: warn when buildPageTree returns empty (blank screen indicator)
  useEffect(() => {
    if (elements.length === 0) {
      console.warn('[AppV2] buildPageTree() returned empty — no elements to render. Check route matching and block loading.')
    }
  }, [elements])

  // Global keyboard dispatcher — allows blocks to bind "document:keydown"
  // (e.g., command-bar ⌘K). Runs once per AppV2 mount.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const root = appRootRef.current
      const target = e.target
      if (root && target instanceof Node && !root.contains(target)) return
      runtime.dispatchEvent('document', 'keydown', {
        key: e.key,
        code: e.code,
        metaKey: e.metaKey,
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
      })
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [runtime])

  // Isolated app style — inject theme token CSS variables for per-app theming.
  // Uses getIsolatedAppStyle() which handles both oklch and legacy HSL formats,
  // and sets --color-* alongside --* so Tailwind utilities resolve correctly.
  // When a ResolvedTheme is provided, its cssVars are merged on top so the new
  // design-system path wins over legacy themeTokens when both are present.
  const tokenStyle = useMemo<React.CSSProperties>(() => {
    const style = getIsolatedAppStyle(themeTokens ?? undefined, colorScheme ?? 'light')
    // Merge ResolvedTheme cssVars on top (new path wins over legacy themeTokens)
    if (theme?.cssVars) {
      Object.assign(
        style,
        resolveThemeCssVarsForScheme(theme, colorScheme ?? 'light'),
      )
    }
    // Background/foreground via CSS variables (no hsl wrapper — oklch is a complete color)
    style.backgroundColor = 'var(--color-background)'
    style.color = 'var(--color-foreground)'
    // Phase 393: consume --font-sans so the wrapper div (and descendants that
    // inherit font-family) actually pick up the preset font. Without this
    // line, setting --font-sans on the wrapper has no visual effect because
    // nothing on the wrapper reads the variable. Parity with v1 ThemedWrapper.
    ;(style as Record<string, string>).fontFamily = 'var(--font-sans)'
    return style as unknown as React.CSSProperties
  }, [themeTokens, theme, colorScheme])

  useEffect(() => {
    if (!portalContainer || !(portalContainer instanceof HTMLElement)) return

    const styleClass = `style-${manifest.style ?? 'luma'}`
    const hadClass = portalContainer.classList.contains(styleClass)
    const previousCompleteAttr = portalContainer.getAttribute('data-cogo-complete-color-tokens')
    const previousColorScheme = portalContainer.style.colorScheme
    const previousBackgroundColor = portalContainer.style.backgroundColor
    const previousColor = portalContainer.style.color
    const previousFontFamily = portalContainer.style.fontFamily
    const previousVars = new Map<string, string>()

    portalContainer.classList.add(styleClass)
    portalContainer.setAttribute('data-cogo-complete-color-tokens', 'true')
    portalContainer.style.colorScheme = String((tokenStyle as Record<string, string>).colorScheme ?? colorScheme ?? 'light')
    portalContainer.style.backgroundColor = 'var(--color-background)'
    portalContainer.style.color = 'var(--color-foreground)'
    portalContainer.style.fontFamily = 'var(--font-sans)'

    for (const [key, value] of Object.entries(tokenStyle as Record<string, string>)) {
      if (!key.startsWith('--')) continue
      previousVars.set(key, portalContainer.style.getPropertyValue(key))
      portalContainer.style.setProperty(key, value)
    }

    return () => {
      if (!hadClass) portalContainer.classList.remove(styleClass)
      if (previousCompleteAttr == null) portalContainer.removeAttribute('data-cogo-complete-color-tokens')
      else portalContainer.setAttribute('data-cogo-complete-color-tokens', previousCompleteAttr)
      portalContainer.style.colorScheme = previousColorScheme
      portalContainer.style.backgroundColor = previousBackgroundColor
      portalContainer.style.color = previousColor
      portalContainer.style.fontFamily = previousFontFamily
      for (const [key, previous] of previousVars) {
        if (previous) portalContainer.style.setProperty(key, previous)
        else portalContainer.style.removeProperty(key)
      }
    }
  }, [manifest.style, tokenStyle, colorScheme, portalContainer])

  // Standalone runtime fallback — replicate the `style-{name}` class and complete token vars onto <html> and <body>
  // so shadcn-preset CSS scoped under `.style-luma .cn-*` reaches Base UI
  // Portal'd content (Popover / Select / Calendar / Dialog / Tooltip / etc.).
  // Portals mount to document.body which is NOT a descendant of the AppV2
  // wrapper div. The style class alone is not enough: cn-* CSS consumes raw
  // `var(--card)`/`var(--primary)`, so html/body must also carry the complete
  // color variables emitted by getIsolatedAppStyle().
  useEffect(() => {
    if (portalContainer || requireDesignSandbox) return

    const styleClass = `style-${manifest.style ?? 'luma'}`
    const root = document.documentElement
    const body = document.body
    const rootHadClass = root.classList.contains(styleClass)
    const bodyHadClass = body.classList.contains(styleClass)
    const previousRootCompleteAttr = root.getAttribute('data-cogo-complete-color-tokens')
    const previousBodyCompleteAttr = body.getAttribute('data-cogo-complete-color-tokens')
    const previousColorScheme = root.style.colorScheme
    const previousVars = new Map<string, string>()

    root.classList.add(styleClass)
    body.classList.add(styleClass)
    root.setAttribute('data-cogo-complete-color-tokens', 'true')
    body.setAttribute('data-cogo-complete-color-tokens', 'true')

    for (const [key, value] of Object.entries(tokenStyle as Record<string, string>)) {
      if (!key.startsWith('--')) continue
      previousVars.set(key, root.style.getPropertyValue(key))
      root.style.setProperty(key, value)
    }
    root.style.colorScheme = String((tokenStyle as Record<string, string>).colorScheme ?? colorScheme ?? 'light')

    return () => {
      if (!rootHadClass) root.classList.remove(styleClass)
      if (!bodyHadClass) body.classList.remove(styleClass)
      if (previousRootCompleteAttr == null) root.removeAttribute('data-cogo-complete-color-tokens')
      else root.setAttribute('data-cogo-complete-color-tokens', previousRootCompleteAttr)
      if (previousBodyCompleteAttr == null) body.removeAttribute('data-cogo-complete-color-tokens')
      else body.setAttribute('data-cogo-complete-color-tokens', previousBodyCompleteAttr)
      for (const [key, previous] of previousVars) {
        if (previous) root.style.setProperty(key, previous)
        else root.style.removeProperty(key)
      }
      root.style.colorScheme = previousColorScheme
    }
  }, [manifest.style, tokenStyle, colorScheme, portalContainer, requireDesignSandbox])


  // Agent-chat dock → shell split view (sipass 4729aba pattern).
  // When the agent-chat block wants to dock (isOpen && isExpanded),
  // the shell switches to `flex` so the page content stays at flex-1
  // and an aria-hidden placeholder sibling reserves the right column.
  // The chat panel itself is still position: fixed so it visually
  // docks to the viewport edge, but the placeholder keeps the flex
  // layout honest — admin content reflows left instead of sliding
  // under the panel. Reading via runtime.resolveSymbol — any app
  // that includes the agent-chat block exposes these symbols; apps
  // without it get `""` (falsy) and behave normally.
  const chatIsOpen = Boolean(runtime.resolveSymbol('#{agentChat.isOpen}'))
  const chatIsExpanded = Boolean(runtime.resolveSymbol('#{agentChat.isExpanded}'))
  const reserveChatColumn = Boolean(agentSlot) && chatIsOpen && chatIsExpanded
  void tick // re-evaluate on every state change via the existing subscribe
  const designSandboxBlocked = requireDesignSandbox && !portalContainer

  if (designSandboxBlocked) {
    return (
      <div
        data-testid="app-v2-design-sandbox-blocked"
        data-cogo-design-sandbox-blocked="true"
        data-cogo-design-sandbox-required="true"
      />
    )
  }

  return (
    <AtomDefaultsProvider value={theme?.atomProps ?? {}}>
      <div data-testid="app-v2-theme-wrapper" style={{ display: 'contents' }}>
        <BreakpointProvider containerRef={appRootRef}>
          <div
            ref={appRootRef}
            data-testid="block-theme-wrapper"
            data-cogo-page-id={motionBoundaryPageId}
            data-cogo-page-block-keys={motionBoundaryPageBlockKeys}
            data-cogo-renderable-block-keys={motionBoundaryRenderableBlockKeys}
            data-cogo-block-exclusions={motionBoundaryBlockExclusions}
            data-cogo-loaded-block-keys={loadedBlockKeys}
            data-cogo-complete-color-tokens="true"
            data-cogo-design-sandboxed={portalContainer ? "true" : "false"}
            data-cogo-design-sandbox-required={requireDesignSandbox ? "true" : "false"}
            data-cogo-render-pack-transport={renderPackBlockCount > 0 ? "primary" : "json"}
            data-cogo-render-pack-blocks={String(renderPackBlockCount)}
            className={`style-${manifest.style ?? 'luma'} bg-background text-foreground min-h-screen ${reserveChatColumn ? 'flex' : ''}`}
            style={tokenStyle}
          >
            <div className={reserveChatColumn ? 'flex-1 min-w-0 flex flex-col' : 'contents'}>
              <RenderContext.Provider value={ctx}>
                <LayoutWrapper layout={(pageSpec?.layout ?? "single") as Layout}>
                  {elements.map((el, i) => {
                    const Renderer = runtime.registry.get(el.type)
                    if (!Renderer) {
                      // Unmapped type at the page-tree top level: warn +
                      // observable placeholder, the same answer the v1
                      // ElementRenderer gives for leaves. Returning null here
                      // dropped a whole page section with no warning and no
                      // trace — a page whose own `ui.root` type is unmapped
                      // rendered as an empty div.
                      return (
                        <React.Fragment key={el.id || i}>
                          {renderUnmappedType(el.type, el.id)}
                        </React.Fragment>
                      )
                    }
                    // A3.4 — derive block id from element id for click-time block identification.
                    // buildPageTree wraps stack-layout blocks in section-<blockName> wrappers;
                    // unwrapped layouts use the block name directly as the element id.
                    const rawId = typeof el.id === 'string' ? el.id : `block-${i}`
                    const blockId = rawId.startsWith('section-') ? rawId.slice('section-'.length) : rawId
                    const motionBlockId = resolveMotionBlockId(blockId, runtimeBlocks[blockId])
                    return (
                      <div
                        key={el.id || i}
                        data-cogo-block-id={blockId}
                        data-cogo-block-namespace={motionBlockId}
                        style={{ display: 'contents' }}
                      >
                        <Renderer element={el as never} />
                      </div>
                    )
                  })}
                </LayoutWrapper>
              </RenderContext.Provider>
            </div>
            {reserveChatColumn && (
              // Placeholder column — mirrors the agent-chat panel's docked
              // width on sm+. Hidden below sm since the panel falls back
              // to a bottom-sheet there.
              <div
                aria-hidden="true"
                className="hidden sm:block shrink-0 w-[400px] md:w-[440px] lg:w-[480px]"
              />
            )}
            {agentSlot}
            {effectiveHostServices && modalRegistry && (
              <ModalHost store={effectiveHostServices.modalStore} registry={modalRegistry} />
            )}
          </div>
        </BreakpointProvider>
      </div>
    </AtomDefaultsProvider>
  )
}
